import React, { useEffect, useRef, useState } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import { Icon } from 'semantic-ui-react';
import ptBr from 'date-fns/locale/pt-BR';
import 'react-datepicker/dist/react-datepicker.css';

import Popover from '../Popover';
import {
  CalendarWrapper,
  DateInput,
  DateInputGroup,
  DateInputLabel,
  DateInputsRow,
  DateRangeFooter,
  DateRangeLayout,
  DateRangeMain,
  DateRangeTrigger,
  DateRangeValue,
  PresetItem,
  PresetList,
  PrimaryButton,
  RelativeWrap,
  TextButton
} from '../styles';
import { DateRangeFilterField, DateRangePresetKey } from '../types';
import {
  DATE_RANGE_PRESETS,
  detectPreset,
  formatDateBR,
  formatDateRangeLabel,
  getPresetByKey,
  normalizeDateRange,
  parseDateBR,
  parseDateOnly,
  toDateOnlyString
} from './dateRangePresets';

registerLocale('ptBr', ptBr);

const toStableKey = (value?: Date | string | null): string =>
  toDateOnlyString(value);

export type DateRangeFieldProps = {
  field: DateRangeFilterField;
  startValue?: Date | string | null;
  endValue?: Date | string | null;
  onChange: (start: Date | null, end: Date | null) => void;
  onApply?: (start: Date | null, end: Date | null) => void;
  disabled?: boolean;
};

const DateRangeField: React.FC<DateRangeFieldProps> = ({
  field,
  startValue,
  endValue,
  onChange,
  onApply,
  disabled
}) => {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const startKey = toStableKey(startValue);
  const endKey = toStableKey(endValue);
  const appliedStart = parseDateOnly(startValue);
  const appliedEnd = parseDateOnly(endValue);

  const [draftStart, setDraftStart] = useState<Date | null>(appliedStart);
  const [draftEnd, setDraftEnd] = useState<Date | null>(appliedEnd);
  const [activeInput, setActiveInput] = useState<'start' | 'end'>('start');
  const [startText, setStartText] = useState(formatDateBR(appliedStart));
  const [endText, setEndText] = useState(formatDateBR(appliedEnd));
  const [selectedPreset, setSelectedPreset] = useState<DateRangePresetKey>(
    detectPreset(appliedStart, appliedEnd)
  );

  const availablePresets = field.presets
    ? DATE_RANGE_PRESETS.filter(preset => field.presets!.includes(preset.key))
    : DATE_RANGE_PRESETS;

  useEffect(() => {
    if (!open) return;
    setDraftStart(appliedStart);
    setDraftEnd(appliedEnd);
    setStartText(formatDateBR(appliedStart));
    setEndText(formatDateBR(appliedEnd));
    setSelectedPreset(detectPreset(appliedStart, appliedEnd));
    setActiveInput('start');
    // Compara pela data de calendário, não pela referência do Date
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, startKey, endKey]);

  const handlePresetClick = (key: DateRangePresetKey) => {
    setSelectedPreset(key);
    if (key === 'custom') {
      setActiveInput('start');
      return;
    }
    const preset = getPresetByKey(key);
    if (!preset) return;
    const range = preset.getRange();
    setDraftStart(range.start);
    setDraftEnd(range.end);
    setStartText(formatDateBR(range.start));
    setEndText(formatDateBR(range.end));
  };

  const handleCalendarChange = (dates: [Date | null, Date | null] | Date | null) => {
    setSelectedPreset('custom');

    if (Array.isArray(dates)) {
      const [start, end] = dates;
      setDraftStart(start);
      setDraftEnd(end);
      setStartText(formatDateBR(start));
      setEndText(formatDateBR(end));
      if (start && !end) setActiveInput('end');
      if (start && end) setActiveInput('start');
      return;
    }

    if (activeInput === 'start') {
      setDraftStart(dates);
      setStartText(formatDateBR(dates));
      if (dates && draftEnd && dates.getTime() > draftEnd.getTime()) {
        setDraftEnd(null);
        setEndText('');
      }
      setActiveInput('end');
    } else {
      setDraftEnd(dates);
      setEndText(formatDateBR(dates));
    }
  };

  const handleStartTextBlur = () => {
    const parsed = parseDateBR(startText);
    if (parsed) {
      setDraftStart(parsed);
      setSelectedPreset('custom');
    } else if (!startText) {
      setDraftStart(null);
    } else {
      setStartText(formatDateBR(draftStart));
    }
  };

  const handleEndTextBlur = () => {
    const parsed = parseDateBR(endText);
    if (parsed) {
      setDraftEnd(parsed);
      setSelectedPreset('custom');
    } else if (!endText) {
      setDraftEnd(null);
    } else {
      setEndText(formatDateBR(draftEnd));
    }
  };

  const commitRange = (search: boolean) => {
    const range = normalizeDateRange(draftStart, draftEnd);
    if (search && onApply) {
      onApply(range.start, range.end);
    } else {
      onChange(range.start, range.end);
    }
    setOpen(false);
  };

  const handleApply = () => {
    commitRange(true);
  };

  const handleCancel = () => {
    setOpen(false);
  };

  const handleClose = () => {
    // Clique fora / Esc: mantém o intervalo escolhido no campo (não descarta)
    commitRange(false);
  };

  const displayValue = formatDateRangeLabel(appliedStart, appliedEnd);

  return (
    <RelativeWrap>
      <DateRangeTrigger
        ref={anchorRef}
        type="button"
        open={open}
        hasValue={Boolean(displayValue)}
        disabled={disabled}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => {
          if (disabled) return;
          if (open) {
            handleClose();
            return;
          }
          setOpen(true);
        }}
      >
        <Icon name="calendar alternate outline" />
        <DateRangeValue>
          {displayValue || field.placeholder || 'Selecione'}
        </DateRangeValue>
      </DateRangeTrigger>

      <Popover
        open={open}
        onClose={handleClose}
        anchorRef={anchorRef}
        width={560}
        align="left"
      >
        <DateRangeLayout>
          <PresetList>
            {availablePresets.map(preset => (
              <PresetItem
                key={preset.key}
                type="button"
                selected={selectedPreset === preset.key}
                onClick={() => handlePresetClick(preset.key)}
              >
                {preset.label}
              </PresetItem>
            ))}
          </PresetList>

          <DateRangeMain>
            <DateInputsRow>
              <DateInputGroup>
                <DateInputLabel>Data de Início</DateInputLabel>
                <DateInput
                  active={activeInput === 'start'}
                  value={startText}
                  placeholder="dd/mm/aaaa"
                  onFocus={() => setActiveInput('start')}
                  onChange={e => setStartText(e.target.value)}
                  onBlur={handleStartTextBlur}
                />
              </DateInputGroup>
              <DateInputGroup>
                <DateInputLabel>Data de Término</DateInputLabel>
                <DateInput
                  active={activeInput === 'end'}
                  value={endText}
                  placeholder="dd/mm/aaaa"
                  onFocus={() => setActiveInput('end')}
                  onChange={e => setEndText(e.target.value)}
                  onBlur={handleEndTextBlur}
                />
              </DateInputGroup>
            </DateInputsRow>

            <CalendarWrapper>
              <DatePicker
                selected={activeInput === 'end' ? draftEnd : draftStart}
                onChange={handleCalendarChange}
                startDate={draftStart}
                endDate={draftEnd}
                selectsStart={activeInput === 'start'}
                selectsEnd={activeInput === 'end'}
                minDate={activeInput === 'end' && draftStart ? draftStart : undefined}
                inline
                locale="ptBr"
                monthsShown={1}
                dateFormat="dd/MM/yyyy"
              />
            </CalendarWrapper>

            <DateRangeFooter>
              <TextButton type="button" onClick={handleCancel}>
                Cancelar
              </TextButton>
              <PrimaryButton type="button" onClick={handleApply}>
                Aplicar
              </PrimaryButton>
            </DateRangeFooter>
          </DateRangeMain>
        </DateRangeLayout>
      </Popover>
    </RelativeWrap>
  );
};

export default DateRangeField;
